import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Crown, Lock, Play, Coins, Sparkles, Edit3, Save, X, Send, Info, MessageCircle, CheckCircle2 } from "lucide-react";

export default function Premium() {
  const { user, refresh } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [active, setActive] = useState(null);
  const [content, setContent] = useState([]);
  const [unlocked, setUnlocked] = useState(false);
  const [msg, setMsg] = useState("");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === "admin";

  const loadProfiles = () => api.get("/premium/profiles").then(r => setProfiles(r.data));
  useEffect(() => { loadProfiles(); }, []);

  const openProfile = async (p) => {
    setActive(p); setEditing(false);
    try {
      const { data } = await api.get(`/premium/profiles/${p.id}/content`);
      setContent(data.content); setUnlocked(data.unlocked);
      // Profile may have been mutated (e.g. instructions) — refresh from server
      if (data.profile) setActive(data.profile);
    } catch (_e) { /* keep previous state */ }
  };

  const unlock = async () => {
    setMsg("");
    try {
      await api.post(`/premium/profiles/${active.id}/unlock`);
      setMsg("Profile unlocked!");
      refresh();
      openProfile(active);
    } catch (e) { setMsg(e?.response?.data?.detail || "Unlock failed"); }
  };

  const startEdit = () => {
    setEditForm({
      name: active.name || "",
      description: active.description || "",
      category: active.category || "",
      price_goldly: active.price_goldly || 100,
      instructions: active.instructions || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/premium/profiles/${active.id}`, {
        ...editForm,
        price_goldly: parseInt(editForm.price_goldly) || 0,
      });
      setActive(data);
      setEditing(false);
      await loadProfiles();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  if (!active) {
    return (
      <div className="px-4 py-6">
        <div className="flex items-center gap-2 mb-1">
          <Crown className="w-7 h-7 text-[#FF7A00]"/>
          <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Premium</h1>
        </div>
        <p className="text-white/60 text-sm mb-5">Exclusive profiles • Pay-per-unlock with Goldly</p>

        <div className="mb-5 p-4 rounded-3xl bg-gradient-to-br from-[#FF7A00]/10 via-[#FF2A5F]/10 to-transparent border border-[#FF7A00]/30 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#FF7A00] flex-shrink-0 mt-0.5"/>
          <div className="text-xs">
            <p className="font-bold text-[#FF7A00] mb-1">How Premium Works</p>
            <p className="text-white/80">Each profile has its own exclusive content. Unlock once with Goldly coins → watch unlimited times.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3" data-testid="premium-profiles">
          {profiles.map(p => (
            <button key={p.id} onClick={()=>openProfile(p)} data-testid={`premium-${p.id}`}
              className="relative aspect-[3/4] rounded-2xl overflow-hidden group border border-white/10 active:scale-95 transition">
              <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/90"/>
              <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-[#FF7A00]/90 text-black text-[10px] font-bold flex items-center gap-1">
                <Coins className="w-3 h-3"/>{p.price_goldly}
              </div>
              <div className="absolute bottom-0 inset-x-0 p-3">
                <div className="text-[10px] uppercase tracking-wide text-[#00E5FF] font-bold">{p.category}</div>
                <div className="font-outfit font-extrabold text-base leading-tight">{p.name}</div>
                <div className="text-[10px] text-white/70 mt-0.5">{p.content_count} videos</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 pb-24">
      <button onClick={()=>{setActive(null); setContent([]); setEditing(false);}} data-testid="premium-back" className="text-[#00E5FF] text-sm mb-3">← Back to profiles</button>

      <div className="rounded-3xl overflow-hidden mb-4 relative h-40">
        <img src={active.cover_image} alt="" className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"/>
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-[#00E5FF] font-bold">{active.category}</div>
            <h2 className="font-outfit font-extrabold text-2xl">{active.name}</h2>
            <p className="text-xs text-white/80 max-w-xs">{active.description}</p>
          </div>
          {isAdmin && !editing && (
            <button onClick={startEdit} data-testid="premium-edit-btn"
              className="px-2.5 py-1.5 rounded-full bg-[#FF7A00]/90 text-black text-[11px] font-bold flex items-center gap-1 active:scale-95">
              <Edit3 className="w-3 h-3"/>Edit
            </button>
          )}
        </div>
      </div>

      {/* Admin Edit Form */}
      {editing && isAdmin && (
        <div className="mb-4 rounded-2xl bg-[#FF7A00]/8 border border-[#FF7A00]/30 p-3 space-y-2" data-testid="premium-edit-form">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-sm flex items-center gap-1"><Edit3 className="w-3.5 h-3.5 text-[#FF7A00]"/>Edit Premium Profile</h3>
            <button onClick={()=>setEditing(false)} data-testid="premium-edit-cancel" className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4"/></button>
          </div>
          <input data-testid="premium-edit-name" value={editForm.name} onChange={e=>setEditForm(f=>({...f, name: e.target.value}))} placeholder="Profile name"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <input data-testid="premium-edit-category" value={editForm.category} onChange={e=>setEditForm(f=>({...f, category: e.target.value}))} placeholder="Category"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <textarea data-testid="premium-edit-description" rows={2} value={editForm.description} onChange={e=>setEditForm(f=>({...f, description: e.target.value}))} placeholder="Short description"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          <textarea data-testid="premium-edit-instructions" rows={5} value={editForm.instructions} onChange={e=>setEditForm(f=>({...f, instructions: e.target.value}))}
            placeholder="📋 Instructions for viewers — what exclusive content is offered at what charges. e.g.&#10;• 1-on-1 video call — 5000 Goldly / 15 min&#10;• Custom shoutout reel — 2000 Goldly&#10;• DM personal coaching — 8000 Goldly / week"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm font-mono"/>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-wider text-white/60 w-28">Unlock Price (Goldly)</label>
            <input data-testid="premium-edit-price" type="number" min={0} value={editForm.price_goldly} onChange={e=>setEditForm(f=>({...f, price_goldly: e.target.value}))}
              className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          </div>
          <button onClick={saveEdit} disabled={saving} data-testid="premium-edit-save"
            className="w-full py-2.5 rounded-full bg-[#FF7A00] text-black font-bold disabled:opacity-50 flex items-center justify-center gap-2 active:scale-95">
            <Save className="w-4 h-4"/>{saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      )}

      {/* Instructions Panel (always visible if instructions present) */}
      {active.instructions && !editing && (
        <div className="mb-4 rounded-2xl bg-gradient-to-br from-[#00E5FF]/10 to-[#A855F7]/10 border border-[#00E5FF]/30 p-3" data-testid="premium-instructions">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Info className="w-4 h-4 text-[#00E5FF]"/>
            <h3 className="font-bold text-sm">Exclusive Content & Charges</h3>
          </div>
          <pre className="whitespace-pre-wrap text-[12px] text-white/85 leading-relaxed font-sans">{active.instructions}</pre>
        </div>
      )}

      {/* Photo gallery slider (up to 10) */}
      {Array.isArray(active.photo_gallery) && active.photo_gallery.length > 0 && !editing && (
        <div className="mb-4" data-testid="premium-photo-gallery">
          <h3 className="font-bold text-sm mb-2 flex items-center gap-1.5">📸 Photo Gallery <span className="text-[10px] text-white/50 font-normal">({active.photo_gallery.length})</span></h3>
          <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-1 -mx-4 px-4">
            {active.photo_gallery.map((url, i) => (
              <img key={i} src={url} alt={`Gallery ${i+1}`} data-testid={`premium-gallery-img-${i}`}
                className="snap-center flex-shrink-0 w-64 h-64 object-cover rounded-2xl border border-white/10"
                loading="lazy"/>
            ))}
          </div>
        </div>
      )}

      {!unlocked && user?.role !== "admin" && (
        <div className="mb-4 p-4 rounded-3xl bg-gradient-to-br from-[#FF7A00]/15 to-[#FF2A5F]/10 border border-[#FF7A00]/30 text-center">
          <Lock className="w-10 h-10 text-[#FF7A00] mx-auto mb-2"/>
          <p className="font-outfit font-bold mb-2">Profile Locked</p>
          <p className="text-xs text-white/70 mb-3">Unlock all exclusive content for <b className="text-[#FF7A00]">{active.price_goldly} Goldly</b></p>
          {msg && <div className="text-sm text-[#FF2A5F] mb-2" data-testid="unlock-msg">{msg}</div>}
          <button onClick={unlock} data-testid="unlock-btn" className="px-6 py-2.5 rounded-full bg-[#FF7A00] text-black font-bold neon-gold active:scale-95">
            Unlock Now
          </button>
        </div>
      )}

      <div className="space-y-4" data-testid="premium-content">
        {content.map(c => (
          <div key={c.id} className="rounded-2xl overflow-hidden border border-white/10 bg-white/5" data-testid={`pcontent-${c.id}`}>
            {unlocked || user?.role === "admin" ? (
              <video src={c.video_url} controls className="w-full aspect-video bg-black" poster={c.thumbnail_url}/>
            ) : (
              <div className="w-full aspect-video bg-black flex items-center justify-center relative">
                <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover opacity-30"/>
                <Lock className="w-10 h-10 text-[#FF7A00] absolute"/>
              </div>
            )}
            <div className="p-3">
              <div className="text-sm font-semibold truncate">{c.title}</div>
              <div className="text-[11px] text-white/50 flex items-center gap-1 mt-0.5"><Play className="w-3 h-3"/>{c.views} views • {Math.round(c.duration_seconds/60)}m</div>
              {/* Purchase inquiry form per video */}
              <PurchaseInquiryForm profileId={active.id} contentId={c.id} contentTitle={c.title}/>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PurchaseInquiryForm({ profileId, contentId, contentTitle }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ message: "", contact_email: user?.email || "", contact_phone: "" });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/premium/inquiry", { profile_id: profileId, content_id: contentId, ...form });
      setSent(true);
      setForm({ message: "", contact_email: user?.email || "", contact_phone: "" });
      setTimeout(() => { setOpen(false); setSent(false); }, 2200);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not send inquiry");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={()=>setOpen(true)} data-testid={`inquiry-open-${contentId}`}
        className="mt-2 w-full py-2 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#00E5FF] text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95">
        <MessageCircle className="w-3.5 h-3.5"/>Interested? Send purchase inquiry
      </button>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`inquiry-form-${contentId}`} className="mt-2 rounded-xl bg-white/5 border border-[#00E5FF]/30 p-2.5 space-y-2">
      <div className="flex items-center justify-between text-[10px] text-white/60">
        <span>Inquiry about: <b className="text-[#00E5FF]">{contentTitle}</b></span>
        <button type="button" onClick={()=>setOpen(false)} className="p-1 rounded hover:bg-white/10"><X className="w-3.5 h-3.5"/></button>
      </div>
      <textarea data-testid={`inquiry-msg-${contentId}`} required rows={3} value={form.message} onChange={e=>setForm(f=>({...f, message: e.target.value}))}
        placeholder="Tell the creator what you're looking for and any details about your purchase…"
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 outline-none text-xs"/>
      <div className="grid grid-cols-2 gap-2">
        <input data-testid={`inquiry-email-${contentId}`} type="email" value={form.contact_email} onChange={e=>setForm(f=>({...f, contact_email: e.target.value}))}
          placeholder="Your email" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 outline-none text-xs"/>
        <input data-testid={`inquiry-phone-${contentId}`} value={form.contact_phone} onChange={e=>setForm(f=>({...f, contact_phone: e.target.value.replace(/[^\d+\-\s]/g,'')}))}
          placeholder="Phone (optional)" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 outline-none text-xs"/>
      </div>
      {err && <div className="text-[#FF2A5F] text-xs" data-testid={`inquiry-err-${contentId}`}>{err}</div>}
      {sent ? (
        <div data-testid={`inquiry-sent-${contentId}`} className="text-emerald-300 text-xs flex items-center gap-1 bg-emerald-500/10 p-2 rounded-lg">
          <CheckCircle2 className="w-3.5 h-3.5"/>Inquiry sent! Our team will be in touch.
        </div>
      ) : (
        <button disabled={busy} data-testid={`inquiry-submit-${contentId}`}
          className="w-full py-2 rounded-full bg-[#00E5FF] text-black font-bold text-xs disabled:opacity-50 flex items-center justify-center gap-1.5 active:scale-95">
          <Send className="w-3.5 h-3.5"/>{busy ? "Sending…" : "Submit Inquiry"}
        </button>
      )}
    </form>
  );
}
